/**
 * Client-side deployment routing hint for ingress (AKS): sends a header on API
 * requests when the user is on free-tier routing. Not a substitute for
 * server-side authorization.
 *
 * Tier is persisted in sessionStorage (cleared on tab close and on logout).
 * Coordinate header name/value with BE and DevOps.
 */

/** HTTP header name — must match ingress + CORS allowlist */
export const DEPLOYMENT_ROUTING_HEADER = "X-ANI-Deployment-Tier";

/** Header value when routing to free-tier pools */
export const DEPLOYMENT_ROUTING_VALUE_FREE = "free";

/** Query parameter name for WebSocket URLs when gateway expects tier (W1) */
export const DEPLOYMENT_ROUTING_WS_QUERY = "deployment_tier";

export type DeploymentRoutingTier = "free" | "standard";

const STORAGE_KEY = "ani_deployment_routing_tier";

/** Dispatched when tier changes so WebSocket layer can reconnect */
export const DEPLOYMENT_ROUTING_CHANGED_EVENT = "ani-deployment-routing-changed";

function safeSessionGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeSessionRemove(key: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function parseTier(raw: string | null): DeploymentRoutingTier | null {
  if (raw === "free" || raw === "standard") return raw;
  return null;
}

/**
 * Current persisted routing tier for this tab, if any.
 */
export function getDeploymentRoutingTier(): DeploymentRoutingTier | null {
  return parseTier(safeSessionGet(STORAGE_KEY));
}

/**
 * Whether free-tier ingress routing should be signaled (REST header / WS query).
 */
export function isFreeDeploymentRouting(): boolean {
  return getDeploymentRoutingTier() === "free";
}

/**
 * Headers to merge into fetch/axios for the API when tier is "free".
 */
export function getDeploymentRoutingHeaders(): Record<string, string> {
  if (isFreeDeploymentRouting()) {
    return { [DEPLOYMENT_ROUTING_HEADER]: DEPLOYMENT_ROUTING_VALUE_FREE };
  }
  return {};
}

/**
 * Merge deployment routing headers into a plain header map for fetch().
 */
export function mergeDeploymentRoutingHeaders(
  base: Record<string, string>
): Record<string, string> {
  return { ...base, ...getDeploymentRoutingHeaders() };
}

/**
 * Append deployment tier query param for WebSocket URLs when tier is free.
 * Uses `&` if the URL already contains `?`.
 */
export function appendDeploymentRoutingQuery(url: string): string {
  if (!isFreeDeploymentRouting()) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${DEPLOYMENT_ROUTING_WS_QUERY}=${DEPLOYMENT_ROUTING_VALUE_FREE}`;
}

function dispatchChangedIfBrowser(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DEPLOYMENT_ROUTING_CHANGED_EVENT));
}

/**
 * Persist tier from GET /security/user/modules (or equivalent).
 *
 * IMPORTANT: If `deployment_routing_tier` is missing or null, we clear any
 * cached tier to avoid stale "free" values persisting across user switches
 * or after logout. Missing tier is treated as "standard" (no routing header).
 */
export function applyDeploymentRoutingTierFromModules(data: {
  deployment_routing_tier?: DeploymentRoutingTier | null;
}): void {
  const v = data.deployment_routing_tier;
  if (v === "free" || v === "standard") {
    setDeploymentRoutingTier(v);
  } else {
    // Clear stale tier if backend returns null/undefined/missing
    clearDeploymentRoutingState();
  }
}

/**
 * Persist tier from auth profile when BE implements pattern D-b.
 *
 * IMPORTANT: If `deployment_routing_tier` is missing or null, we clear any
 * cached tier to avoid stale "free" values persisting across user switches
 * or after logout. Missing tier is treated as "standard" (no routing header).
 */
export function applyDeploymentRoutingTierFromUserProfile(user: {
  deployment_routing_tier?: DeploymentRoutingTier | null;
} | null | undefined): void {
  const v = user?.deployment_routing_tier;
  if (v === "free" || v === "standard") {
    setDeploymentRoutingTier(v);
  } else if (user !== null && user !== undefined) {
    clearDeploymentRoutingState();
  }
}

/**
 * Set tier and notify listeners if the value changed.
 */
export function setDeploymentRoutingTier(tier: DeploymentRoutingTier): void {
  const prev = safeSessionGet(STORAGE_KEY);
  const next = tier;
  if (prev === next) return;
  safeSessionSet(STORAGE_KEY, next);
  dispatchChangedIfBrowser();
}

/**
 * Clear tier (logout, user switch, or unauthenticated).
 */
export function clearDeploymentRoutingState(): void {
  const had = safeSessionGet(STORAGE_KEY);
  safeSessionRemove(STORAGE_KEY);
  if (had !== null) {
    dispatchChangedIfBrowser();
  }
}
